import React, { useEffect, useState, useContext, useMemo } from "react";
import { fetchCustomerDetails, downloadInvoice } from "../../api";
import styles from "./CustomerTransactions.module.css";
import Pagination from "../Pagination/Pagination";
import { SearchContext } from "../../context/SearchContext";

const CustomerTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [loading, setLoading] = useState(true);
  const { searchTerm } = useContext(SearchContext);
  const formatStatus = (status) => {
    if (!status) return "—";
    if (status.toLowerCase() === "successful") return "Successful";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  useEffect(() => {
    const getTransactions = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetchCustomerDetails(token);

        if (response.data && response.data.transactions) {
          setTransactions(response.data.transactions);
        }
      } catch (error) {
        console.error("Failed to fetch transactions:", error);
      } finally {
        setLoading(false);
      }
    };

    getTransactions();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [transactions]);

  const handleInvoiceDownload = async (invoiceNumber) => {
    try {
      const blob = await downloadInvoice(invoiceNumber);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error downloading invoice:", err);
      alert("Failed to download invoice.");
    }
  };

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const filteredTransactions = useMemo(() => {
    if (!searchTerm) return transactions.filter((tx) => tx.invoice_number);
    return transactions
      .filter((tx) => tx.invoice_number)
      .filter((tx) => {
        const name = tx.customer_name?.toLowerCase() || "";
        const description = tx.description?.toLowerCase() || "";
        const status = tx.status?.toLowerCase() || "";
        return (
          name.includes(searchTerm.toLowerCase()) ||
          description.includes(searchTerm.toLowerCase()) ||
          status.includes(searchTerm.toLowerCase())
        );
      });
  }, [transactions, searchTerm]);

  const currentTransactions = filteredTransactions.slice(
    indexOfFirst,
    indexOfLast
  );
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  return (
    <div className={styles.transactionContainer}>
      <h3>Transaction History</h3>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className={styles.transactionTableContainer}>
            <table className={styles.transactionTable}>
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Customer Name</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Description</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.length > 0 ? (
                  currentTransactions.map((tx, index) => (
                    <tr key={tx.transaction_id}>
                      <td>{`T${String(indexOfFirst + index + 1).padStart(
                        3,
                        "0"
                      )}`}</td>
                      <td>{tx.customer_name}</td>
                      <td>
                        {new Date(tx.transaction_date).toLocaleDateString()}
                      </td>
                      <td>
                        <span className="dirham-symbol">&#xea;</span>{" "}
                        {tx.amount ?? 0}
                      </td>
                      <td>{formatStatus(tx.status)}</td>
                      <td>{tx.description}</td>
                      <td>
                        {tx.invoice_number ? (
                          <button
                            className={styles.downloadButton}
                            onClick={() =>
                              handleInvoiceDownload(tx.invoice_number)
                            }
                          >
                            View
                          </button>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center" }}>
                      No transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredTransactions.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CustomerTransactions;
